import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout/Layout';
import StatCard from '../components/Dashboard/StatCard';
import api, { healthCheck, attractionsAPI } from '../services/api';
import './Dashboard.css';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalLocations: 0
  });
  const [systemHealth, setSystemHealth] = useState({
    apiStatus: 'Offline',
    database: 'Disconnected',
    uptime: '0%'
  });
  const [attractions, setAttractions] = useState([]);
  const [appUsers, setAppUsers] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // Check system health
        await healthCheck();
        setSystemHealth({
          apiStatus: 'Online',
          database: 'Connected',
          uptime: '99.9%'
        });

        // Get attractions data
        const attractionsData = await attractionsAPI.getAll({ limit: 10 });
        setAttractions(attractionsData.data || []);
        
        // Get app users data — axios api client attaches the admin Bearer token so the
        // server resolves the tenant (tf_tenant_virq) from the admin JWT via optionalAdminTenant.
        const usersResponse = await api.get('/api/auth/users', { params: { limit: 50 } });
        const usersData = usersResponse.data;
        if (usersData.success) {
          setAppUsers(usersData.data.users || []);
        }
        
        // Get feedbacks data
        const feedbacksResponse = await fetch('/api/v1/feedback?limit=10');
        const feedbacksData = await feedbacksResponse.json();
        if (feedbacksData.success) {
          setFeedbacks(feedbacksData.data || []);
        }
        
        // Set stats
        setStats({
          totalUsers: usersData.success ? usersData.data.pagination.total_users : 0,
          totalLocations: attractionsData.pagination?.total || 0
        });

      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
        setSystemHealth({
          apiStatus: 'Offline',
          database: 'Disconnected',
          uptime: '0%'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <Layout title="Dashboard">
        <div className="loading">Loading dashboard...</div>
      </Layout>
    );
  }

  return (
    <Layout title="Dashboard">
      <div className="dashboard">
        {/* Statistics Cards */}
        <div className="stats-grid">
          <StatCard
            title="stats.total_users"
            value={stats.totalUsers}
            change="→ 0 this week"
            icon="👥"
            color="blue"
          />
          <StatCard
            title="stats.total_locations"
            value={stats.totalLocations}
            change="→ 0 this week"
            icon="📍"
            color="green"
          />
        </div>

        {/* Content Grid */}
        <div className="content-grid">
          {/* Recent Users */}
          <div className="dashboard-section">
            <h3>Recent Users</h3>
            <p className="section-subtitle">Latest user registrations</p>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {appUsers.length > 0 ? (
                    appUsers.slice(0, 10).map((user) => (
                      <tr key={user.id}>
                        <td>{user.name || user.username || 'N/A'}</td>
                        <td>{user.email || 'N/A'}</td>
                        <td>{new Date(user.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3" className="no-data">No users yet</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Recent Feedback */}
          <div className="dashboard-section">
            <h3>Recent Feedback</h3>
            <p className="section-subtitle">Latest user feedback</p>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Rating</th>
                    <th>Category</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {feedbacks.length > 0 ? (
                    feedbacks.slice(0, 10).map((feedback) => (
                      <tr key={feedback.id}>
                        <td>{feedback.user?.name || feedback.user?.email || 'N/A'}</td>
                        <td>
                          <span className="rating">
                            {'★'.repeat(feedback.rating)}{'☆'.repeat(5 - feedback.rating)}
                          </span>
                        </td>
                        <td>{feedback.feedback_category || 'N/A'}</td>
                        <td>{new Date(feedback.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="no-data">No feedback yet</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Recent Attractions */}
          <div className="dashboard-section">
            <h3>Recent Attractions</h3>
            <p className="section-subtitle">Latest added attractions</p>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Category</th>
                    <th>District</th>
                    <th>Added</th>
                  </tr>
                </thead>
                <tbody>
                  {attractions.length > 0 ? (
                    attractions.slice(0, 10).map((attraction) => (
                      <tr key={attraction.id}>
                        <td>{attraction.name || 'N/A'}</td>
                        <td>{attraction.category || 'N/A'}</td>
                        <td>{attraction.district || 'N/A'}</td>
                        <td>{new Date(attraction.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="no-data">No attractions yet</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* System Health */}
          <div className="dashboard-section">
            <h3>System Health</h3>
            <div className="health-stats">
              <div className="health-item">
                <span>API Status</span>
                <span className={`status ${systemHealth.apiStatus.toLowerCase()}`}>
                  {systemHealth.apiStatus}
                </span>
              </div>
              <div className="health-item">
                <span>Database</span>
                <span className={`status ${systemHealth.database.toLowerCase()}`}>
                  {systemHealth.database}
                </span>
              </div>
              <div className="health-item">
                <span>Uptime</span>
                <span className="uptime">{systemHealth.uptime}</span>
              </div>
            </div>
          </div>

        </div>
      </div>
    </Layout>
  );
};

export default Dashboard; 